import { useLocation } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import {
    DetailBadgeRow,
    DetailProfileStats,
    detailStatsFromProfileKpi,
} from '~/ui-rocco/pages/detail-shared';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared/large-screen';
import { ProfessionalDockingBadge } from './ProfessionalDockingBadge';

/**
 * The product page's opening under the banner, on the profile: the practice's
 * badge straddling the banner's lower edge, and on the Overview the profile's
 * views and saves on the line the other pages give their trail — the tab band
 * takes that line here, so the figures sit over its right end, clear of the
 * badge's column, and scroll away with the page while the band stays.
 *
 * On the other tabs the badge follows the reader into the tab band instead —
 * see {@link ProfessionalDockingBadge}. The Overview keeps it on the page.
 *
 * Desktop only, as on the other pages: mobile carries the badge in its own
 * social-profile header.
 */
export const ProfessionalOpeningRow = () => {
    // Through the layout hook, not the route payload: this badge is what a
    // Business Manager logo edit repaints, and the patch is applied there.
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    const initial = layoutData?.initial;
    if (!initial) return null;

    // The figures share the Overview's band line; on every other tab the
    // CTA pair takes that spot, so the figures stand down. Compared against
    // whichever tree is mounted — a hardcoded public path never matches
    // inside the preview, which stood the figures down on every tab there.
    const base = professionalTabBase(initial.URLSegment, pathname);
    const overview = pathname.replace(/\/+$/, '') === base;
    // The profile's own tab base, so the badge does not walk the preview
    // iframe out onto the live site.
    const logo = initial.CompanyLogo
        ? {
              src: initial.CompanyLogo,
              alt: `${initial.Title} logo`,
              background: initial.LogoBackgroundColor,
              link: base,
              ariaLabel: `${initial.Title} profile`,
          }
        : undefined;
    const stats = overview
        ? detailStatsFromProfileKpi(initial.AggregatedPublicKPI)
        : undefined;

    return (
        <>
            {overview ? (
                <DetailBadgeRow
                    // Above the tab band that follows, which is layered for
                    // its sticky run; without this the band paints over the
                    // badge's lower half.
                    className="md:z-[116]"
                    logo={logo}
                />
            ) : logo ? (
                // The other tabs carry the badge into the band with them.
                <ProfessionalDockingBadge {...logo} />
            ) : null}
            {stats ? (
                // Out of the flow, so the band that follows starts on the
                // banner's edge; above the band's own layer so the figures
                // show over its ground while the two share the line.
                <div className="z-[116] w-full max-md:hidden md:relative md:px-[3rem]">
                    <div className={cn(CONTENT_CAP_PADDED, 'md:relative')}>
                        <DetailProfileStats
                            stats={stats}
                            className="md:absolute md:top-[2rem] md:right-[16.5625rem]"
                        />
                    </div>
                </div>
            ) : null}
        </>
    );
};
